import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faCog } from "@fortawesome/pro-solid-svg-icons/faCog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tabs from "antd/es/tabs";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { Button } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { getNotifications, isOnlyMentions } from "@app/store/notification";
import { RootState } from "@app/store/rootReducer";
import MmcNotification from "@app/types/MmcNotification";

import NotificationList from "./component/NotificationList";
import NotificationViewMode from "./enum/NotificationViewMode";
import styles from "./NotificationView.module.scss";

interface Props {
  isOnlyMentions: boolean;
  notifications: MmcNotification[];
  onFetch: (payload: { onlyMentions: boolean }) => void;
  onHide: () => void;
}

const NotificationView = ({ isOnlyMentions, notifications, onFetch, onHide }: Props) => {
  const history = useHistory();
  const intl = useIntl();

  const handleSettingsClick = useCallback(() => {
    history.push(`${Path.SETTINGS}/${SettingPath.PRIVACY_NOTIFICATIONS}`);
    onHide();
  }, [history, onHide]);

  return (
    <div className={styles.container}>
      <Row className={styles.top} justify="space-between">
        <Col className={styles.title}>
          {intl.formatMessage({
            id: "navbar.notifications.list.title",
            defaultMessage: "Notifications",
            description: "Navbar Notifications - title",
          })}
        </Col>
        <Col>
          <Tooltip
            title={intl.formatMessage({
              id: "navbar.notifications.list.settings.tooltip",
              defaultMessage: "Notification preferences",
              description: "Navbar Notifications - list - notification - Go to settings - tooltip",
            })}
          >
            <Button
              icon={<FontAwesomeIcon className={styles.icon} icon={faCog} size="lg" />}
              iconOnly
              onClick={handleSettingsClick}
              type="link"
            />
          </Tooltip>
        </Col>
      </Row>
      <Tabs
        activeKey={isOnlyMentions ? NotificationViewMode.MENTIONS : NotificationViewMode.ALL}
        className={styles.tabs}
        onChange={(key) => onFetch({ onlyMentions: key === NotificationViewMode.MENTIONS })}
      >
        <Tabs.TabPane
          key={NotificationViewMode.ALL}
          tab={intl.formatMessage({
            id: "navbar.notifications.list.tab.all",
            defaultMessage: "All Notifications",
            description: "Navbar Notifications - tabs - All Notifications",
          })}
        />
        <Tabs.TabPane
          key={NotificationViewMode.MENTIONS}
          tab={intl.formatMessage({
            id: "navbar.notifications.list.tab.mentions",
            defaultMessage: "Mentions",
            description: "Navbar Notifications - tabs - Mentions",
          })}
        />
      </Tabs>
      <NotificationList notifications={notifications} onGoToSettings={handleSettingsClick} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOnlyMentions: isOnlyMentions(state),
  notifications: getNotifications(state),
});

export default connect(mapStateToProps)(NotificationView);
